import { useState, useRef, useEffect } from 'react';
import { Terminal, CheckCircle, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = ['CEO', 'CTO', 'SVP of Payments', 'Head of Digital Banking', 'VP of Technology', 'Other'];
const AUM_OPTIONS = ['Under $100M', '$100M – $500M', '$500M – $1B', '$1B – $5B', '$5B – $10B', 'Over $10B'];

const sandboxFeatures = [
  'Isolated test environment — no real money',
  'Pre-funded test wallets with dummy balances',
  'Simulated FedNow instant transfers',
  'QR Pay test merchant flows',
  'Cardless ATM simulation',
  'Full API access with sandbox keys',
  'Provisioned within 24 hours',
];

export default function SandboxSection() {
  const sectionRef = useRef(null);
  const [revealed, setRevealed] = useState(false);
  const [form, setForm] = useState({ full_name: '', institution: '', role: '', email: '', aum: '', test_goals: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setRevealed(true); obs.disconnect(); } },
      { threshold: 0.05 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.institution || !form.role || !form.email || !form.aum) {
      setErrorMsg('Please fill in all required fields.');
      return;
    }
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/sandbox-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setStatus('success');
      } else {
        setErrorMsg(data.detail || 'Something went wrong. Please try again.');
        setStatus('error');
      }
    } catch {
      setErrorMsg('Network error. Please try again.');
      setStatus('error');
    }
  };

  return (
    <section
      id="sandbox"
      data-testid="sandbox-section"
      ref={sectionRef}
      className="relative section-padding bg-black"
      style={{ scrollMarginTop: '80px' }}
    >
      {/* Background grid */}
      <div className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,255,0,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,0,0.02) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />
      <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[500px] h-[500px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(0,255,0,0.04), transparent 70%)' }}
      />

      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* LEFT: Info */}
          <div style={{ opacity: revealed ? 1 : 0, transform: revealed ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.7s ease' }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-[2px] bg-[#00FF00]" />
              <span className="text-[#00FF00] font-mono text-[11px] uppercase tracking-[0.4em]">Developer Access</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-black uppercase text-white leading-none mb-4"
              style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.02em' }}>
              Request
              <br /><span style={{ color: '#00FF00', textShadow: '0 0 30px rgba(0,255,0,0.3)' }}>Sandbox</span>
              <br />Access
            </h2>
            <p className="text-[#A3A3A3] text-base leading-relaxed mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Test the full NexGenPay eWallet stack in an isolated environment before committing. Real API calls, dummy money, zero risk. Your sandbox is provisioned within 24 hours.
            </p>

            <div className="flex flex-col gap-3 mb-8">
              {sandboxFeatures.map((f, i) => (
                <div
                  key={f}
                  className="flex items-center gap-3"
                  style={{
                    opacity: revealed ? 1 : 0,
                    transform: revealed ? 'translateX(0)' : 'translateX(-20px)',
                    transition: `all 0.5s ease ${0.2 + i * 0.07}s`,
                  }}
                >
                  <CheckCircle size={14} className="text-[#00FF00] flex-shrink-0" />
                  <span className="text-[#A3A3A3] text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>{f}</span>
                </div>
              ))}
            </div>

            {/* Terminal preview */}
            <div className="border border-[#1A1A1A] overflow-hidden" style={{ background: '#080808' }}>
              <div className="flex items-center gap-2 px-4 py-2 border-b border-[#1A1A1A]">
                <div className="w-2.5 h-2.5 rounded-full bg-[#333]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#333]" />
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#00FF00' }} />
                <span className="text-[#A3A3A3] font-mono text-[10px] ml-2 uppercase tracking-widest">Sandbox Terminal</span>
              </div>
              <div className="p-4 font-mono text-xs leading-loose" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                <div><span className="text-[#00FF00]">$</span> <span className="text-[#A3A3A3]">nexgenpay sandbox:init --bank="First Community"</span></div>
                <div className="text-[#555]">  Provisioning isolated environment...</div>
                <div className="text-[#555]">  Creating test wallets (x10)...</div>
                <div className="text-[#555]">  Funding test accounts ($10,000 each)...</div>
                <div className="text-[#555]">  Enabling FedNow simulation...</div>
                <div><span className="text-[#00FF00]">✓</span> <span className="text-white">Sandbox ready.</span></div>
                <div className="text-[#A3A3A3] mt-1">  API Key: <span className="text-[#00FF00]">sandbox_sk_••••••••••••</span></div>
                <div className="text-[#A3A3A3]">  Docs:    <span className="text-[#00FF00]">https://api.nexgenpay.co/sandbox</span></div>
              </div>
            </div>
          </div>

          {/* RIGHT: Form */}
          <div style={{ opacity: revealed ? 1 : 0, transform: revealed ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.7s ease 0.15s' }}>
            {status === 'success' ? (
              <div className="border border-[#00FF00] p-10 text-center" style={{ background: 'rgba(0,255,0,0.04)' }}>
                <div className="flex justify-center mb-5">
                  <div className="p-4 border border-[#00FF00]" style={{ background: 'rgba(0,255,0,0.08)' }}>
                    <Terminal size={32} className="text-[#00FF00]" />
                  </div>
                </div>
                <h3 className="text-white font-black text-lg uppercase mb-3" style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '14px' }}>
                  Request Received
                </h3>
                <p className="text-[#A3A3A3] text-sm leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Your sandbox environment will be provisioned within 24 hours. Check your inbox for credentials and access details.
                </p>
                <div className="mt-6 p-4 border border-[#1A1A1A]" style={{ background: '#0A0A0A' }}>
                  <div className="text-[#00FF00] font-mono text-[10px] uppercase tracking-widest">What Happens Next</div>
                  <div className="text-[#A3A3A3] text-xs mt-2 leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Our team will configure your isolated environment, generate your sandbox API keys, and send setup instructions to your email.
                  </div>
                </div>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="sandbox-form"
                className="border border-[#1A1A1A] p-8"
                style={{ background: '#0A0A0A' }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <Terminal size={16} className="text-[#00FF00]" />
                  <span className="text-white font-bold text-xs uppercase tracking-widest" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    Sandbox Access Request
                  </span>
                </div>

                <div className="flex flex-col gap-4">
                  {/* Full Name */}
                  <div>
                    <label className="block text-[#A3A3A3] font-mono text-[10px] uppercase tracking-widest mb-2">Full Name *</label>
                    <input
                      name="full_name"
                      value={form.full_name}
                      onChange={handleChange}
                      placeholder="Jane Smith"
                      data-testid="sandbox-input-name"
                      className="w-full bg-black border border-[#1A1A1A] text-white text-sm px-4 py-3 focus:outline-none focus:border-[#00FF00] transition-colors duration-200 placeholder-[#333]"
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                    />
                  </div>

                  {/* Institution */}
                  <div>
                    <label className="block text-[#A3A3A3] font-mono text-[10px] uppercase tracking-widest mb-2">Institution *</label>
                    <input
                      name="institution"
                      value={form.institution}
                      onChange={handleChange}
                      placeholder="First Community Bank"
                      data-testid="sandbox-input-institution"
                      className="w-full bg-black border border-[#1A1A1A] text-white text-sm px-4 py-3 focus:outline-none focus:border-[#00FF00] transition-colors duration-200 placeholder-[#333]"
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                    />
                  </div>

                  {/* Role + AUM */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#A3A3A3] font-mono text-[10px] uppercase tracking-widest mb-2">Your Role *</label>
                      <select
                        name="role"
                        value={form.role}
                        onChange={handleChange}
                        data-testid="sandbox-select-role"
                        className="w-full bg-black border border-[#1A1A1A] text-white text-sm px-4 py-3 focus:outline-none focus:border-[#00FF00] transition-colors duration-200"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                      >
                        <option value="">Select</option>
                        {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[#A3A3A3] font-mono text-[10px] uppercase tracking-widest mb-2">Assets (AUM) *</label>
                      <select
                        name="aum"
                        value={form.aum}
                        onChange={handleChange}
                        data-testid="sandbox-select-aum"
                        className="w-full bg-black border border-[#1A1A1A] text-white text-sm px-4 py-3 focus:outline-none focus:border-[#00FF00] transition-colors duration-200"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                      >
                        <option value="">Select</option>
                        {AUM_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-[#A3A3A3] font-mono text-[10px] uppercase tracking-widest mb-2">Work Email *</label>
                    <input
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="jane@firstcommunitybank.com"
                      data-testid="sandbox-input-email"
                      className="w-full bg-black border border-[#1A1A1A] text-white text-sm px-4 py-3 focus:outline-none focus:border-[#00FF00] transition-colors duration-200 placeholder-[#333]"
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                    />
                  </div>

                  {/* Test Goals */}
                  <div>
                    <label className="block text-[#A3A3A3] font-mono text-[10px] uppercase tracking-widest mb-2">What Do You Want to Test? <span className="opacity-50">(Optional)</span></label>
                    <textarea
                      name="test_goals"
                      value={form.test_goals}
                      onChange={handleChange}
                      rows={3}
                      placeholder="e.g. FedNow instant transfers, QR merchant pay, SMS onboarding..."
                      data-testid="sandbox-input-goals"
                      className="w-full bg-black border border-[#1A1A1A] text-white text-sm px-4 py-3 focus:outline-none focus:border-[#00FF00] transition-colors duration-200 placeholder-[#333] resize-none"
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                    />
                  </div>

                  {errorMsg && (
                    <div className="text-red-400 text-xs font-mono" data-testid="sandbox-error">{errorMsg}</div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    data-testid="sandbox-submit-btn"
                    className="btn-neon w-full flex items-center justify-center gap-2 text-xs"
                    style={{ fontFamily: 'Unbounded, sans-serif', padding: '16px 24px' }}
                  >
                    {status === 'loading' ? (
                      <span className="flex items-center gap-2">
                        <span className="w-3 h-3 border border-black border-t-transparent rounded-full animate-spin" />
                        Provisioning...
                      </span>
                    ) : (
                      <>Request Sandbox Access <ChevronRight size={14} /></>
                    )}
                  </button>

                  <p className="text-[#333] text-[10px] text-center font-mono">
                    Sandbox environments provisioned within 24 hours &bull; No credit card required
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
